import { api } from "./client";

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;

const getAuthHeaders = () => {
  const lang = localStorage.getItem("lang") || "en";
  const token = localStorage.getItem("token");

  return {
    "Content-Type": "application/json",
    Authorization: `Bearer ${token}`,
    "Accept-Language": lang,
  };
};

// -----------------------------
// PURCHASE CRUD
// -----------------------------
export const createPurchase = async (purchaseData) => {
  try {
    const response = await api.post(
      `${API_BASE_URL}api/purchases/add`,
      purchaseData,
      {
        headers: getAuthHeaders(),
      }
    );
    return response.data;
  } catch (error) {
    console.error("Create Purchase API Error:", error);
    throw error?.response?.data || { message: "Failed to create purchase" };
  }
};

export const getAllPurchases = async (params = {}) => {
  try {
    const response = await api.get(`${API_BASE_URL}api/purchases/list`, {
      headers: getAuthHeaders(),
      params,
    });
    return response.data;
  } catch (error) {
    console.error("Get Purchases API Error:", error);
    throw error?.response?.data || { message: "Failed to fetch purchases" };
  }
};

export const getPurchaseById = async (id) => {
  try {
    const response = await api.get(`${API_BASE_URL}api/purchases/${id}`, {
      headers: getAuthHeaders(),
    });
    return response.data;
  } catch (error) {
    console.error("Get Purchase By ID API Error:", error);
    throw error?.response?.data || { message: "Failed to fetch purchase details" };
  }
};

export const updatePurchase = async (id, purchaseData) => {
  try {
    const response = await api.put(
      `${API_BASE_URL}api/purchases/${id}`,
      purchaseData,
      {
        headers: getAuthHeaders(),
      }
    );
    return response.data;
  } catch (error) {
    console.error("Update Purchase API Error:", error);
    throw error?.response?.data || { message: "Failed to update purchase" };
  }
};

export const deletePurchase = async (id) => {
  try {
    const response = await api.delete(`${API_BASE_URL}api/purchases/${id}`, {
      headers: getAuthHeaders(),
    });
    return response.data;
  } catch (error) {
    console.error("Delete Purchase API Error:", error);
    throw error?.response?.data || { message: "Failed to delete purchase" };
  }
};

// -----------------------------
// HELPERS FOR FORM DROPDOWNS
// Adjust these endpoints only if your project uses different route names
// -----------------------------
export const getAllSuppliersForPurchase = async () => {
  try {
    const response = await api.get(`${API_BASE_URL}api/supplier/list`, {
      headers: getAuthHeaders(),
    });
    return response.data;
  } catch (error) {
    console.error("Get Suppliers API Error:", error);
    throw error?.response?.data || { message: "Failed to fetch suppliers" };
  }
};

export const getAllProductsForPurchase = async () => {
  try {
    const response = await api.get(`${API_BASE_URL}api/products/product-list`, {
      headers: getAuthHeaders(),
    });
    return response.data;
  } catch (error) {
    console.error("Get Products API Error:", error);
    throw error?.response?.data || { message: "Failed to fetch products" };
  }
};

export const getAllWarehousesForPurchase = async () => {
  try {
    const response = await api.get(`${API_BASE_URL}api/warehouse/list`, {
      headers: getAuthHeaders(),
    });
    return response.data;
  } catch (error) {
    console.error("Get Warehouses API Error:", error);
    throw error?.response?.data || { message: "Failed to fetch warehouses" };
  }
};

// -----------------------------
// DATA NORMALIZERS
// -----------------------------
export const getLocalizedValue = (value, lang = "en") => {
  if (!value) return "";
  if (typeof value === "string") return value;
  return value?.[lang] || value?.en || value?.ar || "";
};

export const getSupplierDisplayName = (supplier, lang = "en") => {
  if (!supplier) return "";
  const firstName = getLocalizedValue(supplier.firstName, lang);
  const lastName = getLocalizedValue(supplier.lastName, lang);
  return `${firstName} ${lastName}`.trim();
};

export const getProductId = (product) => {
  if (!product) return null;
  return product._id || product.id || null;
};

export const getProductSku = (product) => {
  if (!product) return "";
  return product.sku || product.SKU || "";
};

export const getProductDisplayName = (product, lang = "en") => {
  if (!product) return "";
  const fromNameFields = getLocalizedValue(
    product.productName ||
      product.Product_Name ||
      product.name ||
      product.Name ||
      product.title,
    lang
  );

  if (fromNameFields) return fromNameFields;

  return getProductSku(product) || "";
};

export const mapPurchaseToTableRow = (purchase, lang = "en") => {
  return {
    key: purchase?._id,
    _id: purchase?._id,
    supplierName: getSupplierDisplayName(purchase?.supplier, lang) || "-",
    reference: purchase?.reference || "-",
    date: purchase?.purchaseDate
      ? new Date(purchase.purchaseDate).toLocaleDateString()
      : "-",
    status: purchase?.status || "-",
    grandTotal: `$${Number(purchase?.grandTotal || 0).toFixed(2)}`,
    paid: `$${Number(purchase?.paid || 0).toFixed(2)}`,
    due: `$${Number(purchase?.due || 0).toFixed(2)}`,
    paymentStatus: purchase?.paymentStatus || "-",
    raw: purchase,
  };
};
